import { CircleCheck, Languages, Link2, ShieldCheck, View } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ComponentType, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useDemandesSite, type MoyenContactSite } from '../../api/demandeSite.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { DemandeSiteEnCours, useDateDemande } from './DemandeSiteEnCours.js';
import { DemandeSiteFormulaire } from './DemandeSiteFormulaire.js';
import styles from './DemandeSitePage.module.css';

type Avis =
  { readonly type: 'accuse'; readonly moyen: MoyenContactSite } | { readonly type: 'dejaEnCours' };

const POINTS: readonly {
  readonly cle: 'nouveau' | 'relie' | 'langues' | 'visite';
  readonly icone: ComponentType<{ size?: number; strokeWidth?: number; 'aria-hidden'?: boolean }>;
}[] = [
  { cle: 'nouveau', icone: ShieldCheck },
  { cle: 'relie', icone: Link2 },
  { cle: 'langues', icone: Languages },
  { cle: 'visite', icone: View },
];

/**
 * Site — une prestation à part, sur devis. Aucun compte de galerie n'édite
 * plus le site depuis la régie : la page présente la prestation et reçoit la
 * demande, que l'équipe Artosera suit dans la console. L'éditeur
 * (`SitePage`) reste réservé à l'accès de support.
 */
export function DemandeSitePage(): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie, compte } = useShellContext();
  const reponse = useDemandesSite(galerieSlug);
  const date = useDateDemande();
  const [avis, setAvis] = useState<Avis | null>(null);
  const avisRef = useRef<HTMLDivElement>(null);

  // L'accusé remplace un formulaire long : on y amène le regard et le lecteur d'écran.
  useEffect(() => {
    if (avis) avisRef.current?.focus();
  }, [avis]);

  const support = galerie.support;
  const peutEnvoyer = galerieALaction(galerie, 'settings:write') && !support;
  const enCours = reponse.data?.enCours ?? null;
  const derniere = reponse.data?.demandes[0];
  const derniereClose =
    enCours === null && derniere && (derniere.etat === 'traitee' || derniere.etat === 'abandonnee')
      ? derniere
      : null;

  return (
    <div className={styles.page}>
      <header className={styles.entete}>
        <h1 className={styles.titre}>{t('commun:navigation.site')}</h1>
        <p className={styles.intro}>{t('regie:demandeSite.intro')}</p>
      </header>

      {support ? (
        <p className={styles.support} role="note">
          <span>{t('regie:demandeSite.support.bandeau')}</span>
          {galerieAModule(galerie, 'site') ? (
            <Link to={`/${galerieSlug}/site`}>{t('regie:demandeSite.support.lienEditeur')}</Link>
          ) : null}
        </p>
      ) : null}

      <div className={styles.grille}>
        <section className={styles.panneau} aria-labelledby="demande-site-prestation">
          <h2 id="demande-site-prestation" className={styles.titrePanneau}>
            {t('regie:demandeSite.prestation.titre')}
          </h2>
          <ul className={styles.points}>
            {POINTS.map(({ cle, icone: Icone }) => (
              <li key={cle} className={styles.point}>
                <span className={styles.pointIcone}>
                  <Icone size={18} strokeWidth={1.5} aria-hidden />
                </span>
                <div>
                  <p className={styles.pointTitre}>
                    {t(`regie:demandeSite.prestation.${cle}.titre`)}
                  </p>
                  <p className={styles.aide}>{t(`regie:demandeSite.prestation.${cle}.texte`)}</p>
                </div>
              </li>
            ))}
          </ul>
          <p className={styles.devis}>{t('regie:demandeSite.prestation.devis')}</p>
        </section>

        <div className={styles.colonne}>
          <div role="status" className={styles.statut}>
            {avis ? (
              <div ref={avisRef} tabIndex={-1} className={styles.accuse}>
                <CircleCheck size={20} strokeWidth={1.5} aria-hidden />
                <p>
                  {avis.type === 'accuse'
                    ? t('regie:demandeSite.accuse', {
                        moyen: t(`regie:demandeSite.recontact.${avis.moyen}`),
                      })
                    : t('regie:demandeSite.erreurs.dejaEnCours')}
                </p>
              </div>
            ) : null}
          </div>

          {reponse.isLoading ? (
            <p className={styles.vide}>{t('commun:chargement')}</p>
          ) : reponse.isError || !reponse.data ? (
            <p className={styles.vide} role="alert">
              {estApiError(reponse.error) ? reponse.error.message : t('commun:erreurReseau')}
            </p>
          ) : enCours ? (
            <DemandeSiteEnCours demande={enCours} />
          ) : (
            <>
              {derniereClose ? (
                <p className={styles.derniere}>
                  {t('regie:demandeSite.derniere', {
                    etat: t(
                      `regie:demandeSite.etatsClos.${derniereClose.etat as 'traitee' | 'abandonnee'}`,
                    ),
                    date: date(derniereClose.traiteeLe ?? derniereClose.envoyeeLe),
                  })}
                </p>
              ) : null}
              {peutEnvoyer ? (
                <section className={styles.panneau}>
                  <DemandeSiteFormulaire
                    galerieSlug={galerieSlug}
                    nomInitial={compte.nom}
                    emailInitial={compte.email}
                    onEnvoyee={(demande) =>
                      setAvis({ type: 'accuse', moyen: demande.contact.moyen })
                    }
                    onDejaEnCours={() => setAvis({ type: 'dejaEnCours' })}
                  />
                </section>
              ) : (
                <p className={styles.panneau}>{t('regie:demandeSite.directionSeule')}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
